import { ActionEvent, Controller } from "@hotwired/stimulus"
import { type TurboBeforeFrameRenderEvent } from "@hotwired/turbo"

import * as Ga4 from "@/helpers/ga4_helper"
import { isDesktop } from "@/helpers/window_size"
import SearchModalController from "./search_modal_controller"

const ITEM_LIST_NAME = "Venue Packages"
const ITEM_LIST_ID = "venue_packages"

export default class extends Controller {
  static outlets = ["search-modal"]
  declare readonly searchModalOutlet: SearchModalController

  static targets = [
    "dateInput",
    "experienceRadio",
    "filtersForm",
    "hiddenTreatmentInput",
    "treatmentCheckbox",
    "treatmentsDialog"
  ]
  declare readonly dateInputTarget: HTMLInputElement
  declare readonly experienceRadioTargets: HTMLInputElement[]
  declare readonly filtersFormTarget: HTMLFormElement
  declare readonly hiddenTreatmentInputTarget: HTMLInputElement
  declare readonly treatmentCheckboxTargets: HTMLInputElement[]

  static values = { ecommerceItems: Array }
  declare readonly ecommerceItemsValue: Ga4.EcommerceItem[]

  initialize(): void {
    // Toggle mobile sticky status bar on intersection

    const packagesSection = document.getElementById("packages")
    const viewPackagesButton = document.getElementById("js-view-packages-button")!
    let packagesViewed = false

    document.documentElement.style.setProperty(
      "--view-packages-height",
      `${viewPackagesButton.getBoundingClientRect().height}px`
    )

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting }) => {
          // Show/hide status bar
          viewPackagesButton?.classList.toggle("is-hidden", isIntersecting)

          // Fire GA4 event if first time seeing packages
          if (isIntersecting && !packagesViewed) {
            packagesViewed = true

            Ga4.viewItemListEvent(ITEM_LIST_NAME, ITEM_LIST_ID, this.ecommerceItemsValue)
          }
        })
      },
      { root: null, rootMargin: "-25% 0px", threshold: 0 }
    )

    if (packagesSection) {
      observer.observe(packagesSection)
    }
  }

  applyExperienceFilter({ params: { filterName } }: ActionEvent): void {
    this.disableAnyExperienceOnSubmit()
    this.filtersFormTarget.requestSubmit()
    Ga4.selectContentEvent("Experiences", filterName)
  }

  applyTreatmentFilter({ params: { filterName }, target }: ActionEvent): void {
    this.disableAnyExperienceOnSubmit()

    const selectedValues = this.treatmentCheckboxTargets.flatMap((checkbox) =>
      checkbox.checked ? [checkbox.value] : []
    )
    this.hiddenTreatmentInputTarget.value = selectedValues.join("-")

    this.filtersFormTarget.requestSubmit()

    if ((target as HTMLInputElement).checked) {
      Ga4.selectContentEvent("Treatments", filterName)
    }
  }

  buyMonetaryVoucher(): void {
    const item = {
      item_id: "mv_001",
      item_name: "Monetary Voucher",
      item_brand: "Spabreaks",
      item_category: "Monetary Voucher",
      item_list_id: "monetary_voucher_banner",
      item_list_name: "Monetary Voucher Banner",
      index: 0,
      price: 100,
      quantity: 1
    }

    Ga4.selectItemEvent(item, "voucher")
    Ga4.addToCartEvent(item, item.price, "voucher")
  }

  preserveTreatmentsDialog(event: TurboBeforeFrameRenderEvent): void {
    // Preserve visibility state of treatments dialog from old to new frame. Normally we'd use
    // `data-turbo-permanent` but contents of dialog need updated frame values.
    const oldFrame = event.target as Element
    const newFrame = event.detail.newFrame as Element
    const targetSelector = `[data-${this.identifier}-target="treatmentsDialog"]`
    const oldDialog = oldFrame?.querySelector<HTMLElement>(targetSelector)
    const newDialog = newFrame?.querySelector<HTMLElement>(targetSelector)

    if (oldDialog && newDialog) {
      newDialog.hidden = oldDialog.hidden
    }
  }

  resetAllTreatmentFilters(): void {
    this.treatmentCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
  }

  resetTreatmentFilter({ params: { treatmentId } }: ActionEvent): void {
    const selectedTreatment = this.treatmentCheckboxTargets.find(
      (checkbox) => checkbox.value === treatmentId.toString() && checkbox.checked
    )

    selectedTreatment?.click()
  }

  focusDateFilter(): void {
    if (isDesktop()) {
      this.dateInputTarget.focus()
    } else {
      this.searchModalOutlet.openAndFocusDate()
    }
  }

  clearTreatmentsFilters(): void {
    // Un-check without `change` event, otherwise form will submit on each one.
    this.treatmentCheckboxTargets.forEach((checkbox) => (checkbox.checked = false))
    this.hiddenTreatmentInputTarget.value = ""

    this.filtersFormTarget.requestSubmit()
  }

  private disableAnyExperienceOnSubmit(): void {
    // Prevents failed param validation on BE, since experience param can't be empty
    const selectedExperience = this.experienceRadioTargets.find((radio) => radio.checked)!
    selectedExperience.disabled = selectedExperience.value === ""
  }
}
